import {
  createContext,
  use,
  useState,
  useCallback,
  useEffect,
  useRef,
  type ReactNode,
} from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import pl from './locales/pl.json';
import en from './locales/en.json';
import uk from './locales/uk.json';
import { localeFromPath } from '@/i18n/paths';
import { localeNavigation } from '@/i18n/locale-navigation';

const LOCALES = ['pl', 'en', 'uk'] as const;
export type Locale = (typeof LOCALES)[number];
export type TranslationKey = keyof typeof pl;

const messages: Record<Locale, Record<string, string>> = { pl, en, uk };

const COOKIE_NAME = 'bookeper_locale';

function readCookie(): Locale | null {
  const match = document.cookie.match(new RegExp(`(?:^|; )${COOKIE_NAME}=([^;]*)`));
  if (match && LOCALES.includes(match[1] as Locale)) return match[1] as Locale;
  return null;
}

function writeCookie(locale: Locale) {
  document.cookie = `${COOKIE_NAME}=${locale};path=/;max-age=${365 * 24 * 3600};SameSite=Lax`;
}

/** Detect initial locale: URL prefix > "pl"; unprefixed routes are canonical Polish URLs. */
function detectLocale(pathname: string): Locale {
  const fromUrl = localeFromPath(pathname);
  if (fromUrl) return fromUrl;

  return 'pl';
}

interface I18nContextValue {
  locale: Locale;
  setLocale: (locale: Locale) => void;
  t: (key: TranslationKey, params?: Record<string, string | number>) => string;
}

const I18nContext = createContext<I18nContextValue | null>(null);

export function I18nProvider({ children }: { children: ReactNode }) {
  const routerLocation = useLocation();
  const navigate = useNavigate();
  const { pathname, search, hash } = routerLocation;
  const [locale, setLocaleState] = useState<Locale>(() => detectLocale(pathname));
  const previousPathnameRef = useRef(pathname);

  // Sync locale when URL changes externally (browser back/forward, external link).
  // TODO(react-doctor P4): replace this setState-in-effect with a pure derivation
  // backed by `useSyncExternalStore`. The current logic depends on tracking the
  // previous pathname to force "pl" when navigating away from a locale-prefixed
  // path, which is awkward to express via render-time derivation alone.
  useEffect(() => {
    const previousPathname = previousPathnameRef.current;
    if (previousPathname === pathname) return;

    previousPathnameRef.current = pathname;
    const urlLocale = localeFromPath(pathname);
    if (urlLocale && urlLocale !== locale) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setLocaleState(urlLocale);
      return;
    }

    if (!urlLocale && locale !== 'pl') {
      setLocaleState('pl');
    }
  }, [locale, pathname]);

  const setLocale = useCallback(
    (newLocale: Locale) => {
      setLocaleState(newLocale);
      writeCookie(newLocale);
      const destination = localeNavigation(pathname, newLocale);
      const newPath = destination.path + search + hash;
      if (destination.fullPage) {
        window.location.assign(newPath);
        return;
      }
      // Language switch is the same page in another locale — preserve the
      // reader's position (ScrollToTop skips resets carrying this flag).
      navigate(newPath, { state: { preserveScroll: true } });
    },
    [hash, navigate, pathname, search]
  );

  const t = useCallback(
    (key: TranslationKey, params?: Record<string, string | number>): string => {
      let value = messages[locale]?.[key] ?? messages.pl[key] ?? key;
      if (params) {
        Object.entries(params).forEach(([k, v]) => {
          value = value.replace(new RegExp(`\\{${k}\\}`, 'g'), String(v));
        });
      }
      return value;
    },
    [locale]
  );

  return <I18nContext.Provider value={{ locale, setLocale, t }}>{children}</I18nContext.Provider>;
}

export function useTranslation() {
  const ctx = use(I18nContext);
  if (!ctx) throw new Error('useTranslation must be used within I18nProvider');
  return ctx;
}
